import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link, useSearchParams } from 'react-router-dom'
import { useState } from 'react'
import { api, Load } from '../lib/api'
import { useAuth } from '../lib/auth-context'
import PremiumHeader from '../components/PremiumHeader'

const STATUS_COLORS: Record<string, string> = {
  POSTED: 'bg-blue-100 text-blue-800',
  ASSIGNED: 'bg-purple-100 text-purple-800',
  IN_PROGRESS: 'bg-orange-100 text-orange-800',
  IN_TRANSIT: 'bg-orange-100 text-orange-800',
  DELIVERED: 'bg-green-100 text-green-800',
  CANCELLED: 'bg-gray-100 text-gray-600',
  PENDING: 'bg-yellow-100 text-yellow-800',
}

export default function LoadsPage() {
  const qc = useQueryClient()
  const [searchParams] = useSearchParams()
  const { hasRole } = useAuth()
  const viewerParam = String(searchParams.get('viewer') ?? '').trim().toLowerCase()
  const viewerMode: 'carrier' | 'driver' | 'internal' | undefined =
    viewerParam === 'carrier' || viewerParam === 'driver' || viewerParam === 'internal'
      ? (viewerParam as 'carrier' | 'driver' | 'internal')
      : undefined
  const isRestrictedViewer = viewerMode === 'carrier' || viewerMode === 'driver'
  const isViewerDriver = viewerMode === 'driver'

  const canSeeInternal = !isRestrictedViewer && hasRole('SUPER_ADMIN', 'AUX_ADMIN', 'ADMIN', 'CARRIER_ADMIN', 'DISPATCHER')
  const canDelete = !isRestrictedViewer && hasRole('SUPER_ADMIN', 'AUX_ADMIN', 'ADMIN', 'CARRIER_ADMIN', 'DISPATCHER', 'BROKER_ADMIN')
  const canSeeCustomer = !isRestrictedViewer && (canSeeInternal || hasRole('BROKER', 'BROKER_ADMIN', 'DEALER', 'SHIPPER'))
  const canSeeCarrierMoney = !isRestrictedViewer && canSeeInternal
  const isDriver = hasRole('DRIVER')
  const [message, setMessage] = useState<string | null>(null)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const { data: loads, isLoading, error } = useQuery({
    queryKey: ['loads', viewerMode ?? 'default'],
    queryFn: async () => {
      const r = await api.loads.list({ viewer: viewerMode })
      return r.data
    },
    refetchInterval: 15000,
  })

  const deleteMut = useMutation({
    mutationFn: (loadId: string) => api.loads.delete(loadId),
    onSuccess: () => {
      setErrorMessage(null)
      setMessage('Load deleted.')
      qc.invalidateQueries({ queryKey: ['loads'] })
    },
    onError: (err: any) => {
      setMessage(null)
      setErrorMessage(err?.response?.data?.error ?? 'Failed to delete load.')
    },
  })

  if (isLoading) return (
    <div className="flex items-center justify-center min-h-[60vh]">
      <div className="text-gray-400">Loading loads…</div>
    </div>
  )

  if (error) return (
    <div className="container mx-auto p-6">
      <div className="bg-red-50 border border-red-200 text-red-700 p-4 rounded-xl text-sm">
        Failed to load data. Check your connection and try again.
      </div>
    </div>
  )

  return (
    <div className="container mx-auto p-6">
      <PremiumHeader
        title="Loads"
        subtitle="Build, post, assign, and track vehicle moves from a single board."
        primaryAction={
          hasRole('BROKER_ADMIN', 'ADMIN', 'AUX_ADMIN', 'DEALER', 'SHIPPER') ? (
            <Link to="/loads/new" className="bg-blue-700 hover:bg-blue-800 text-white text-sm font-semibold px-4 py-2 rounded-lg transition-colors">
              + Post Load
            </Link>
          ) : undefined
        }
      />

      {message && (
        <div className="mb-4 bg-green-50 border border-green-200 text-green-700 p-3 rounded-xl text-sm">{message}</div>
      )}
      {errorMessage && (
        <div className="mb-4 bg-red-50 border border-red-200 text-red-700 p-3 rounded-xl text-sm">{errorMessage}</div>
      )}

      {!loads || loads.length === 0 ? (
        <div className="bg-white rounded-xl border text-center py-16">
          <p className="text-gray-400 mb-3">No loads on the board</p>
          {hasRole('BROKER_ADMIN', 'ADMIN', 'AUX_ADMIN', 'DEALER', 'SHIPPER') && (
            <Link to="/loads/new" className="text-blue-700 underline text-sm">Post your first load</Link>
          )}
        </div>
      ) : (
        <div className="space-y-3">
          {loads.map((load: Load) => (
            <Link key={load.id} to={`/loads/${load.id}`}
              className="block bg-white rounded-xl border hover:border-blue-300 hover:shadow-sm transition p-4">
              <div className="flex justify-between items-start">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-2">
                    <span className={`px-2 py-0.5 rounded text-xs font-medium ${STATUS_COLORS[load.status] ?? 'bg-gray-100'}`}>
                      {load.status}
                    </span>
                    {load.loadNumber && <span className="text-xs text-gray-400 font-mono">{load.loadNumber}</span>}
                    <span className="text-xs text-gray-400">{(load.vehicles?.length ?? 0)} vehicle{(load.vehicles?.length ?? 0) !== 1 ? 's' : ''}</span>
                  </div>
                  <div className="text-sm text-gray-900 font-medium truncate">
                    {load.origin} → {load.destination}
                  </div>
                  {(load.pickupDate || load.deliveryDate) && (
                    <div className="text-xs text-gray-400 mt-1">
                      {load.pickupDate && `Pickup: ${new Date(load.pickupDate).toLocaleDateString()}`}
                      {load.pickupDate && load.deliveryDate && ' · '}
                      {load.deliveryDate && `Delivery: ${new Date(load.deliveryDate).toLocaleDateString()}`}
                    </div>
                  )}
                  {typeof load.estDistanceMiles === 'number' && (
                    <div className="text-xs text-gray-500 mt-1">Distance: {load.estDistanceMiles.toLocaleString()} mi</div>
                  )}
                  </div>
                  {canDelete && !isDriver && (
                    <div className="mt-3">
                      <button
                        type="button"
                        onClick={(e) => {
                          e.preventDefault()
                          e.stopPropagation()
                          setMessage(null)
                          setErrorMessage(null)
                          if (!confirm('Are you sure you want to delete this load?')) return
                          deleteMut.mutate(load.id)
                        }}
                        disabled={deleteMut.isPending}
                        className="text-xs rounded border border-red-300 px-2 py-1 text-red-700 hover:bg-red-50 disabled:opacity-50"
                      >
                        Delete
                      </button>
                    </div>
                  )}
                {(canSeeCustomer && !isDriver) && (
                  <div className="text-right ml-4">
                    {typeof load.customerPayCents === 'number' && (
                      <div className="text-sm text-gray-700">Customer: ${(load.customerPayCents / 100).toLocaleString(undefined, { maximumFractionDigits: 2 })}</div>
                    )}
                    {typeof load.customerRatePerMileCents === 'number' && (
                      <div className="text-xs text-gray-600">Customer RPM: ${(load.customerRatePerMileCents / 100).toFixed(2)}/mi</div>
                    )}
                    {canSeeCarrierMoney && typeof load.carrierPayCents === 'number' && (
                      <div className="text-sm text-gray-700">Carrier: ${(load.carrierPayCents / 100).toLocaleString(undefined, { maximumFractionDigits: 2 })}</div>
                    )}
                    {canSeeCarrierMoney && typeof load.carrierRatePerMileCents === 'number' && (
                      <div className="text-xs text-gray-600">Carrier RPM: ${(load.carrierRatePerMileCents / 100).toFixed(2)}/mi</div>
                    )}
                    {canSeeInternal && typeof load.grossMarginCents === 'number' && (
                      <div className="text-sm font-semibold text-blue-700">
                        Margin: ${(load.grossMarginCents / 100).toLocaleString(undefined, { maximumFractionDigits: 2 })}
                        {typeof load.marginPercent === 'number' ? ` / ${(load.marginPercent * 100).toFixed(2)}%` : ''}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  )
}
